/**
 * The per-check rows inside a target card.
 *
 * Each row is the check's name, whether it passed, its latency, and - on the
 * authenticated view - the detail string the check itself wrote. The sparkline beside
 * it is that one check's latency over the window, because "redis is up" and "redis has
 * been getting slower for an hour" are different facts and only the second one warns
 * you in advance.
 *
 * On the public view there is no detail to show, and the row says so rather than
 * leaving a blank that reads as "nothing to report".
 */

import { Lock, RefreshCw } from 'lucide-react'

import type {
  HistorySample,
  LatencyThresholds,
  ServiceHealth,
  SiteInfo,
  TargetResult,
} from '../api/types'
import { cn } from '../lib/cn'
import { formatMs } from '../lib/format'
import { latencyBand, latencyFill, resolveThresholds } from '../lib/status'
import { Sparkline } from './charts/Sparkline'
import { ServicePill } from './ui/StatusPill'

interface ServiceListProps {
  result: TargetResult
  samples: HistorySample[]
  /** The dashboard-wide bands, used when `site` is unknown or names none of its own. */
  thresholds: LatencyThresholds
  /**
   * The site this target belongs to, for its per-check latency bands - the reason a
   * slow SMTP row can be amber here while the same latency on `redis` is red.
   */
  site?: SiteInfo
  /**
   * Re-probe this target now. When given, each service row shows a "check now" button.
   * There is no per-service probe - the endpoint returns every service in one response -
   * so any row's button re-fetches the whole target and all rows update together. The
   * per-row placement is convenience: you press it next to the service you care about.
   */
  onCheckNow?: () => void
  isChecking?: boolean
}

export function ServiceList({
  result,
  samples,
  thresholds,
  site,
  onCheckNow,
  isChecking,
}: ServiceListProps) {
  if (result.services.length === 0) {
    return (
      <p className="px-4 py-3 text-xs text-ink-muted">
        {result.reachable
          ? 'The response listed no services.'
          : 'No checks to show — the endpoint did not answer.'}
      </p>
    )
  }

  // Important checks first: a failing database matters more than a failing queue
  // snapshot, and unknown importance sorts with the important ones.
  const ordered = [...result.services].sort((a, b) => {
    const weight = (service: ServiceHealth) => (service.important === false ? 1 : 0)
    return weight(a) - weight(b) || a.name.localeCompare(b.name)
  })

  return (
    <ul className="divide-y">
      {ordered.map((service) => (
        <ServiceRow
          key={service.name}
          service={service}
          samples={samples}
          // Resolved per row, not once for the list: each check may name its own bands.
          thresholds={resolveThresholds(thresholds, site, service.name)}
          view={result.view}
          onCheckNow={onCheckNow}
          isChecking={isChecking}
        />
      ))}
    </ul>
  )
}

function ServiceRow({
  service,
  samples,
  thresholds,
  view,
  onCheckNow,
  isChecking,
}: {
  service: ServiceHealth
  samples: HistorySample[]
  /** Already resolved for this check by the list - the row just reads them. */
  thresholds: LatencyThresholds
  view: TargetResult['view']
  onCheckNow?: () => void
  isChecking?: boolean
}) {
  const band = latencyBand(service.latency_ms, thresholds)
  const trend = samples
    .map((sample) => ({ t: sample.t, value: sample.svc[service.name] ?? null }))
    .filter((point) => point.value !== null)

  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-2 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_7rem_9rem]">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-medium text-ink">{service.name}</span>
          <ServicePill status={service.status} />
          {service.important === false ? (
            <span className="rounded-full border px-1.5 py-px text-[10px] text-ink-muted">
              non-critical
            </span>
          ) : null}
          {onCheckNow ? (
            <button
              type="button"
              onClick={onCheckNow}
              disabled={isChecking}
              title="Re-probe this target now (refreshes every service)"
              aria-label={`Check ${service.name} now`}
              className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] text-ink-secondary transition-colors hover:bg-surface-sunken disabled:opacity-60"
            >
              <RefreshCw size={10} aria-hidden className={isChecking ? 'animate-spin' : undefined} />
              Check now
            </button>
          ) : null}
        </div>

        {service.detail ? (
          <p className="mt-1 text-xs leading-relaxed break-words text-ink-secondary">
            {service.detail}
          </p>
        ) : view === 'public' ? (
          <p className="mt-1 flex items-center gap-1 text-xs text-ink-muted">
            <Lock size={11} aria-hidden />
            Detail and importance need an API key
          </p>
        ) : null}
      </div>

      <div className="text-right sm:text-left">
        <p
          className="tabular text-sm font-semibold"
          style={{ color: band === 'fast' || band === null ? 'var(--color-ink)' : latencyFill(band) }}
        >
          {formatMs(service.latency_ms)}
        </p>
        <p className="text-[11px] text-ink-muted">check latency</p>
      </div>

      <div className="col-span-2 sm:col-span-1">
        {trend.length > 1 ? (
          <Sparkline
            points={trend}
            height={34}
            fill={false}
            valueLabel={`${service.name} latency`}
            ariaLabel={`${service.name} latency over ${trend.length} samples`}
          />
        ) : (
          <p className={cn('text-[11px] text-ink-muted', 'sm:text-right')}>
            Building history…
          </p>
        )}
      </div>
    </li>
  )
}
